import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { useEffect, useId, useState, type FormEvent } from "react";
import { formatDate } from "../format.js";
import { RepertoireSearchPicker } from "../RepertoirePicker.js";
import { addStaffPracticeTodo } from "../practiceTodos.js";
import {
  addRepertoireEntry,
  createRepertoireLibraryPiece,
  getStaffRepertoire,
  getStaffRepertoireMaterials,
  REPERTOIRE_STATUS_LABELS,
  setRepertoireMaterialShared,
  updateRepertoireEntry,
  type RepertoireEntry,
  type RepertoireInput,
  type RepertoireLibraryPiece,
  type RepertoireMaterial,
  type RepertoireStatus,
  type RepertoireList,
} from "../repertoire.js";
import { useAsync, type Async } from "../useAsync.js";
import { Drawer } from "../ui/Dialog.js";
import "./studentProgressJourney.css";

const ACTIVE_STATUSES: RepertoireStatus[] = [
  "learning",
  "polishing",
  "performance_ready",
  "paused",
];

function pieceName(entry: RepertoireEntry): string {
  return entry.composer ? `${entry.composer} — ${entry.title}` : entry.title;
}

export function StudentRepertoire({ studentId, focusEntryId, onChanged, source }: {
  studentId: string;
  focusEntryId?: string;
  onChanged?: () => void;
  source?: Async<RepertoireList>;
}) {
  const [completedPage, setCompletedPage] = useState(1);
  const [archivedPage, setArchivedPage] = useState(1);
  const firstPage = completedPage === 1 && archivedPage === 1;
  const loadedRepertoire = useAsync(
    () => source && firstPage ? Promise.resolve(null) : getStaffRepertoire(studentId, completedPage, archivedPage),
    [studentId, completedPage, archivedPage],
  );
  const repertoire = source && firstPage ? source : loadedRepertoire;
  const [editing, setEditing] = useState<RepertoireEntry | "new" | null>(null);
  const [practiceFor, setPracticeFor] = useState<RepertoireEntry | null>(null);
  const [materialsFor, setMaterialsFor] = useState<RepertoireEntry | null>(null);

  useEffect(() => {
    setCompletedPage(1);
    setArchivedPage(1);
    setEditing(null);
    setPracticeFor(null);
    setMaterialsFor(null);
  }, [studentId]);

  const data = repertoire.data;

  return (
    <div className="stack">
      {!focusEntryId && <div className="spread" style={{ alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 260px" }}>
          <h2 style={{ marginBottom: 4 }}>Repertoire</h2>
          <p className="small muted" style={{ marginTop: 0 }}>
            A durable record of what this student is learning, preparing and has completed.
          </p>
        </div>
        <button type="button" onClick={() => setEditing("new")}>Add repertoire</button>
      </div>}

      {repertoire.loading && !data && <p className="empty">Loading repertoire…</p>}
      {repertoire.error && <div className="error">{repertoire.error}</div>}

      {data && !focusEntryId && <div className="panel" aria-label="Repertoire summary">
        <div className="row" style={{ flexWrap: "wrap", gap: 14 }}>
          <span><strong>{data.counts.learning}</strong> <span className="small muted">Learning</span></span>
          <span><strong>{data.counts.polishing}</strong> <span className="small muted">Polishing</span></span>
          <span><strong>{data.counts.performance_ready}</strong> <span className="small muted">Performance-ready</span></span>
          <span><strong>{data.counts.completed}</strong> <span className="small muted">Completed</span></span>
          {data.counts.paused > 0 && (
            <span><strong>{data.counts.paused}</strong> <span className="small muted">Paused</span></span>
          )}
        </div>
      </div>}

      {editing !== null && (
        <RepertoireEditor key={editing === "new" ? "new" : editing.id} studentId={studentId}
          entry={editing === "new" ? null : editing} onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); repertoire.reload(); if (source && repertoire !== source) source.reload(); onChanged?.(); }} />
      )}

      {data && (
        <>
          {ACTIVE_STATUSES.map((status) => {
            const items = data.current.filter((entry) => entry.status === status && (!focusEntryId || entry.id === focusEntryId));
            if (items.length === 0) return null;
            return (
              <section key={status} aria-labelledby={focusEntryId ? undefined : `repertoire-${status}`} aria-label={focusEntryId ? "Repertoire details" : undefined}>
                {!focusEntryId && <h3 id={`repertoire-${status}`}>{REPERTOIRE_STATUS_LABELS[status]}</h3>}
                <div className="stack repertoire-list">
                  {items.map((entry) => (
                    <RepertoireRow
                      key={entry.id}
                      entry={entry}
                      onEdit={() => setEditing(entry)}
                      onPractice={() => setPracticeFor(entry)}
                      onMaterials={() => setMaterialsFor(entry)}
                    />
                  ))}
                </div>
              </section>
            );
          })}

          {data.current.length === 0 && data.completed.total === 0 && (data.archived?.total ?? 0) === 0 && (
            <div className="panel"><p className="empty">No repertoire has been recorded yet.</p></div>
          )}

          {practiceFor && (
            <PracticeTaskFromRepertoire
              key={practiceFor.id}
              studentId={studentId}
              entry={practiceFor}
              onClose={() => setPracticeFor(null)}
            />
          )}

          {materialsFor && (
            <StudentRepertoireMaterials
              key={materialsFor.id}
              studentId={studentId}
              entry={materialsFor}
              onClose={() => setMaterialsFor(null)}
            />
          )}

          {data.completed.total > 0 && (!focusEntryId || data.completed.items.some((entry) => entry.id === focusEntryId)) && (
            <section aria-labelledby="repertoire-completed-heading">
              <h3 id="repertoire-completed-heading">Completed repertoire</h3>
              <div className="stack repertoire-list">
                {data.completed.items.filter((entry) => !focusEntryId || entry.id === focusEntryId).map((entry) => (
                  <RepertoireRow
                    key={entry.id}
                    entry={entry}
                    onEdit={() => setEditing(entry)}
                    onPractice={() => setPracticeFor(entry)}
                    onMaterials={() => setMaterialsFor(entry)}
                  />
                ))}
              </div>
              {!focusEntryId && data.completed.pages > 1 && (
                <div className="row repertoire-pagination">
                  <button
                    type="button"
                    className="quiet"
                    disabled={data.completed.page <= 1}
                    onClick={() => setCompletedPage((page) => Math.max(1, page - 1))}
                  >
                    Previous
                  </button>
                  <span className="small muted">
                    Page {data.completed.page} of {data.completed.pages}
                  </span>
                  <button
                    type="button"
                    className="quiet"
                    disabled={data.completed.page >= data.completed.pages}
                    onClick={() => setCompletedPage((page) => page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </section>
          )}
          {data.archived && data.archived.total > 0 && (!focusEntryId || data.archived.items.some((entry) => entry.id === focusEntryId)) && (
            <section aria-labelledby="repertoire-archived-heading">
              <h3 id="repertoire-archived-heading">Archived repertoire</h3>
              <div className="stack repertoire-list">
                {data.archived.items.filter((entry) => !focusEntryId || entry.id === focusEntryId).map((entry) => (
                  <ArchivedRepertoireRow key={entry.id} entry={entry} />
                ))}
              </div>
              {!focusEntryId && data.archived.pages > 1 && (
                <div className="row repertoire-pagination">
                  <button type="button" className="quiet" disabled={data.archived.page <= 1}
                    onClick={() => setArchivedPage((page) => Math.max(1, page - 1))}>Previous</button>
                  <span className="small muted">Page {data.archived.page} of {data.archived.pages}</span>
                  <button type="button" className="quiet" disabled={data.archived.page >= data.archived.pages}
                    onClick={() => setArchivedPage((page) => page + 1)}>Next</button>
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

export function StudentRepertoireSnapshot({ studentId, createRequest, repertoire }: {
  studentId: string;
  createRequest: number;
  repertoire: Async<RepertoireList>;
}) {
  const headingId = useId();
  const [manager, setManager] = useState<{ kind: "all" | "new" | "piece"; entryId?: string } | null>(null);
  useEffect(() => {
    if (createRequest > 0) setManager({ kind: "new" });
  }, [createRequest]);
  const data = repertoire.error ? null : repertoire.data;
  const current = data?.current ?? [];
  const visible = ACTIVE_STATUSES.flatMap((status) => current.filter((entry) => entry.status === status)).slice(0, 3);
  return <section className="panel student-progress-summary" aria-labelledby={headingId}>
    <div className="student-progress-summary-head"><h3 id={headingId}>Current repertoire{data && <span className="muted"> · {current.length}</span>}</h3><button type="button" className="student-progress-detail-link" onClick={() => setManager({ kind: "all" })}>All pieces</button></div>
    {repertoire.loading && !data && <p className="small muted" role="status">Loading repertoire…</p>}
    {repertoire.error && <p className="small" role="alert">Repertoire could not be loaded. <button type="button" className="student-progress-detail-link" onClick={repertoire.reload}>Retry</button></p>}
    {data && current.length === 0 && <p className="small muted">No current repertoire yet.</p>}
    {visible.map((entry) => <article className="student-progress-summary-row" key={entry.id}>
      <div className="student-progress-summary-title"><strong>{entry.title}</strong><span className={`tag${entry.status === "learning" ? " warn" : ""}`}>{REPERTOIRE_STATUS_LABELS[entry.status]}</span></div>
      {entry.tutorNote && <p className="small muted student-progress-excerpt">{entry.tutorNote}</p>}
      {entry.createdByName && <p className="small muted">Added by {entry.createdByName} · recorded {formatDate(entry.createdAt)}</p>}
      <div className="student-progress-summary-foot"><span className="small muted">{entry.composer ?? entry.instrument ?? "Repertoire"}</span><button type="button" className="student-progress-detail-link" aria-label={`Details and materials for ${pieceName(entry)}`} onClick={() => setManager({ kind: "piece", entryId: entry.id })}>Details &amp; materials</button></div>
    </article>)}
    {current.length > visible.length && <p className="small muted">Showing {visible.length} of {current.length} current pieces.</p>}
    {manager && <Drawer title={manager.kind === "new" ? "Add repertoire" : manager.kind === "all" ? "Repertoire" : current.find((entry) => entry.id === manager.entryId)?.title ?? "Repertoire details"} size="large" onClose={() => setManager(null)} className="student-progress-drawer">
      {manager.kind === "new"
        ? <RepertoireEditor studentId={studentId} entry={null} onCancel={() => setManager(null)}
            onSaved={() => { repertoire.reload(); setManager(null); }} />
        : <StudentRepertoire key={`${manager.kind}:${manager.entryId ?? ""}`} studentId={studentId} focusEntryId={manager.entryId} source={repertoire} />}
    </Drawer>}
  </section>;
}

function ArchivedRepertoireRow({ entry }: { entry: RepertoireEntry }) {
  return (
    <article className="panel">
      <strong>{pieceName(entry)}</strong>
      {entry.workOrCollection && <div className="small muted">{entry.workOrCollection}</div>}
      <div className="small muted progress-meta">
        {REPERTOIRE_STATUS_LABELS[entry.status]}
        {entry.instrument && ` · ${entry.instrument}`}
        {entry.startedOn && ` · started ${formatDate(entry.startedOn)}`}
        {entry.completedOn && ` · completed ${formatDate(entry.completedOn)}`}
        {entry.archivedAt && ` · archived ${formatDate(entry.archivedAt)}`}
      </div>
      <div className="small muted progress-meta">
        {entry.createdByName ? `Originally added by ${entry.createdByName}` : "Original tutor attribution unavailable"}
        {entry.createdAt && ` · recorded ${formatDate(entry.createdAt)}`}
      </div>
      <div className="small muted">{entry.familyVisible ? "Visible in family archived history" : "Hidden from family"}</div>
    </article>
  );
}

function RepertoireRow({
  entry,
  onEdit,
  onPractice,
  onMaterials,
}: {
  entry: RepertoireEntry;
  onEdit: () => void;
  onPractice: () => void;
  onMaterials: () => void;
}) {
  return (
    <article className="panel">
      <div className="spread" style={{ gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          <strong>{pieceName(entry)}</strong>
          {entry.workOrCollection && <div className="small muted">{entry.workOrCollection}</div>}
          <div className="small muted progress-meta">
            {REPERTOIRE_STATUS_LABELS[entry.status]}
            {entry.instrument && ` · ${entry.instrument}`}
            {entry.startedOn && ` · started ${formatDate(entry.startedOn)}`}
            {entry.completedOn && ` · completed ${formatDate(entry.completedOn)}`}
          </div>
          <div className="small muted progress-meta">
            {entry.createdByName ? `Added by ${entry.createdByName}` : "Tutor attribution unavailable"}
            {entry.createdAt && ` · recorded ${formatDate(entry.createdAt)}`}
          </div>
          {entry.tutorNote && <p className="notes" style={{ marginBottom: 0 }}>{entry.tutorNote}</p>}
          {entry.familyVisible === false && (
            <div className="small muted" style={{ marginTop: 6 }}>Hidden from family</div>
          )}
        </div>
        <div className="row" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button type="button" className="quiet small" onClick={onEdit}>Edit</button>
          <button type="button" className="quiet small" onClick={onMaterials}>Materials</button>
          <button type="button" className="quiet small" onClick={onPractice}>Add practice task</button>
        </div>
      </div>
    </article>
  );
}

function RepertoireEditor({
  studentId,
  entry,
  onCancel,
  onSaved,
}: {
  studentId: string;
  entry: RepertoireEntry | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [libraryPieceId, setLibraryPieceId] = useState<string | null>(entry?.libraryPieceId ?? null);
  const [title, setTitle] = useState(entry?.title ?? "");
  const [composer, setComposer] = useState(entry?.composer ?? "");
  const [workOrCollection, setWorkOrCollection] = useState(entry?.workOrCollection ?? "");
  const [instrument, setInstrument] = useState(entry?.instrument ?? "");
  const [status, setStatus] = useState<RepertoireStatus>(entry?.status ?? "learning");
  const [startedOn, setStartedOn] = useState(entry?.startedOn ?? "");
  const [completedOn, setCompletedOn] = useState(entry?.completedOn ?? "");
  const [tutorNote, setTutorNote] = useState(entry?.tutorNote ?? "");
  const [familyVisible, setFamilyVisible] = useState(entry?.familyVisible ?? true);
  const [keepInFamilyHistory, setKeepInFamilyHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || title.trim() === "") return;
    setBusy(true);
    setProblem(null);
    try {
      let resolvedLibraryPieceId = libraryPieceId;
      const publishToLibrary = (event.nativeEvent as SubmitEvent).submitter?.getAttribute("value") === "shared-library";
      if (publishToLibrary && !resolvedLibraryPieceId) {
        const created = await createRepertoireLibraryPiece({
          title: title.trim(),
          composer: composer || null,
          workOrCollection: workOrCollection || null,
          instrument: instrument || null,
        });
        resolvedLibraryPieceId = created.id;
        setLibraryPieceId(created.id);
      }
      const input: RepertoireInput = {
        libraryPieceId: resolvedLibraryPieceId,
        title,
        composer: composer || null,
        workOrCollection: workOrCollection || null,
        instrument: instrument || null,
        status,
        startedOn: startedOn || undefined,
        completedOn: status === "completed" ? (completedOn || undefined) : null,
        tutorNote: tutorNote || null,
        familyVisible,
      };
      if (entry) await updateRepertoireEntry(studentId, entry.id, input);
      else await addRepertoireEntry(studentId, input);
      onSaved();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not save repertoire.");
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    if (!entry || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await updateRepertoireEntry(studentId, entry.id, { archived: true, familyVisible: familyVisible && keepInFamilyHistory });
      onSaved();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not archive repertoire.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel stack" onSubmit={submit} aria-label={entry ? "Edit repertoire" : "Add repertoire"}>
      <div className="spread" style={{ flexWrap: "wrap", gap: 12 }}>
        <h3 style={{ margin: 0 }}>{entry ? "Edit repertoire" : "Add repertoire"}</h3>
        <button type="button" className="quiet" onClick={onCancel} disabled={busy}>Close</button>
      </div>

      <RepertoireLibraryPicker
        selectedId={libraryPieceId}
        initialLabel={entry?.libraryPieceId ? pieceName(entry) : null}
        onSelect={(piece) => {
          setLibraryPieceId(piece.id);
          setTitle(piece.title);
          setComposer(piece.composer ?? "");
          setWorkOrCollection(piece.workOrCollection ?? "");
          setInstrument(piece.instrument ?? "");
        }}
        onClear={() => setLibraryPieceId(null)}
      />
      <label>
        <span className="small">Title</span>
        <input required maxLength={300} value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>
      <div className="row" style={{ alignItems: "flex-start", flexWrap: "wrap" }}>
        <label style={{ flex: "1 1 220px" }}>
          <span className="small">Composer (optional)</span>
          <input maxLength={300} value={composer} onChange={(event) => setComposer(event.target.value)} />
        </label>
        <label style={{ flex: "1 1 220px" }}>
          <span className="small">Work or collection (optional)</span>
          <input maxLength={300} value={workOrCollection} onChange={(event) => setWorkOrCollection(event.target.value)} />
        </label>
        <label style={{ flex: "1 1 180px" }}>
          <span className="small">Instrument (optional)</span>
          <input maxLength={300} value={instrument} onChange={(event) => setInstrument(event.target.value)} />
        </label>
      </div>
      {!libraryPieceId && (
        <p className="small muted" style={{ margin: 0 }}>
          Save keeps these details on this student's record. “Save and add to shared library” also makes the piece searchable by all tutors.
          {" "}Hub policy: files and teaching assets must not contain personal information.
        </p>
      )}
      <div className="row" style={{ alignItems: "flex-start", flexWrap: "wrap" }}>
        <label style={{ flex: "1 1 180px" }}>
          <span className="small">Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value as RepertoireStatus)}>
            {Object.entries(REPERTOIRE_STATUS_LABELS).map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>
        <label style={{ flex: "1 1 180px" }}>
          <span className="small">Started date</span>
          <input type="date" value={startedOn} onChange={(event) => setStartedOn(event.target.value)} />
        </label>
        {status === "completed" && (
          <label style={{ flex: "1 1 180px" }}>
            <span className="small">Completed date</span>
            <input type="date" value={completedOn} onChange={(event) => setCompletedOn(event.target.value)} />
          </label>
        )}
      </div>
      <label>
        <span className="small">Tutor note (private)</span>
        <textarea
          rows={3}
          maxLength={4000}
          value={tutorNote}
          onChange={(event) => setTutorNote(event.target.value)}
          placeholder="Teaching note for staff; this text is never returned by the family API."
        />
      </label>
      <label className="row repertoire-archive-choice">
        <input
          type="checkbox"
          checked={familyVisible}
          onChange={(event) => setFamilyVisible(event.target.checked)}
        />
        <span>Show this repertoire entry to the family/student</span>
      </label>
      {problem && <div className="error small">{problem}</div>}
      {entry && familyVisible && (
        <label className="row repertoire-archive-choice">
          <input
            type="checkbox"
            checked={keepInFamilyHistory}
            onChange={(event) => setKeepInFamilyHistory(event.target.checked)}
            disabled={busy}
          />
          <span>Keep this item and its shared materials visible in the family&apos;s archived history</span>
        </label>
      )}
      {entry && (
        <p className="small muted">
          Archive hides this item and its shared materials from the family by default. Staff can always view its archived history.
        </p>
      )}
      <div className="row repertoire-editor-actions">
        <div>
          {entry && (
            <button type="button" className="quiet" disabled={busy} onClick={archive}>
              Archive
            </button>
          )}
        </div>
        <div className="row repertoire-form-row">
          <button type="button" className="quiet" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy || title.trim() === ""}>{busy ? "Saving…" : "Save"}</button>
          {!libraryPieceId && <button type="submit" className="quiet" value="shared-library" disabled={busy || !title.trim()}>Save and add to shared library</button>}
        </div>
      </div>
    </form>
  );
}

function RepertoireLibraryPicker({
  selectedId,
  initialLabel,
  onSelect,
  onClear,
}: {
  selectedId: string | null;
  initialLabel: string | null;
  onSelect: (piece: RepertoireLibraryPiece) => void;
  onClear: () => void;
}) {
  if (selectedId) {
    return (
      <div className="panel" style={{ padding: 12 }}>
        <div className="spread" style={{ gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <strong>Linked to repertoire library</strong>
            {initialLabel && <div className="small muted">{initialLabel}</div>}
          </div>
          <button type="button" className="quiet small" onClick={onClear}>
            Choose another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="panel stack" style={{ padding: 12 }}>
      <RepertoireSearchPicker
        onSelect={onSelect}
        label="Find in repertoire library"
        placeholder="Start typing a song, artist, composer or instrument…"
        emptyMessage="No existing match. You can enter the details below for this student's record."
      />
      <div className="small muted">
        Choose a shared library piece to reuse its teaching material, or enter details below for this student.
      </div>
    </div>
  );
}

function materialKind(material: RepertoireMaterial): string {
  if (material.presentationKind === "link") return "Web link";
  if (material.presentationKind === "hub_asset") return "Hub asset";
  return material.presentationKind.toUpperCase();
}

function StudentRepertoireMaterials({
  studentId,
  entry,
  onClose,
}: {
  studentId: string;
  entry: RepertoireEntry;
  onClose: () => void;
}) {
  const materials = useAsync(
    () => getStaffRepertoireMaterials(studentId, entry.id),
    [studentId, entry.id],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function change(material: RepertoireMaterial, shared: boolean) {
    setBusyId(material.id);
    setProblem(null);
    try {
      await setRepertoireMaterialShared(studentId, entry.id, material.id, shared);
      materials.reload();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "Could not change material sharing.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="panel stack" aria-label={`Teaching material for ${pieceName(entry)}`}>
      <div className="spread" style={{ gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0 }}>Teaching material for {pieceName(entry)}</h3>
          <p className="small muted" style={{ marginBottom: 0 }}>
            Tick only the master-library material this student should be able to see.
          </p>
        </div>
        <button type="button" className="quiet" onClick={onClose}>Close</button>
      </div>
      {!entry.libraryPieceId && (
        <div className="notice small">
          This is a legacy repertoire entry. Edit it and choose or create its master repertoire item before sharing material.
        </div>
      )}
      {problem && <div className="error small">{problem}</div>}
      {materials.loading && !materials.data && <p className="empty">Loading teaching material…</p>}
      {materials.error && <div className="error small">{materials.error}</div>}
      {materials.data && materials.data.length === 0 && entry.libraryPieceId && (
        <p className="empty">No master teaching material has been added to this repertoire item yet.</p>
      )}
      {materials.data && materials.data.length > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          {materials.data.map((material) => (
            <label className="panel row" key={material.id} style={{ padding: 10, justifyContent: "flex-start", alignItems: "flex-start" }}>
              <input
                type="checkbox"
                checked={material.shared === true}
                disabled={busyId === material.id}
                onChange={(event) => void change(material, event.target.checked)}
              />
              <span>
                <strong>{material.title}</strong>
                <span className="small muted" style={{ display: "block" }}>
                  {materialKind(material)}{material.description ? ` · ${material.description}` : ""}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
    </section>
  );
}

function PracticeTaskFromRepertoire({
  studentId,
  entry,
  onClose,
}: {
  studentId: string;
  entry: RepertoireEntry;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(`Practise ${entry.title}`);
  const [description, setDescription] = useState("");
  const [suggestedMinutes, setSuggestedMinutes] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || title.trim() === "") return;
    setBusy(true);
    setProblem(null);
    try {
      await addStaffPracticeTodo(studentId, {
        title,
        description: description || null,
        repertoireId: entry.id,
        suggestedMinutes: suggestedMinutes === "" ? null : Number(suggestedMinutes),
      });
      setSaved(true);
      setTitle("");
      setDescription("");
      setSuggestedMinutes("");
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not add that practice task.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel stack" onSubmit={submit} aria-label={`Add practice task for ${pieceName(entry)}`}>
      <div className="spread" style={{ flexWrap: "wrap", gap: 12 }}>
        <div style={{ flex: "1 1 220px" }}>
          <h3 style={{ margin: 0 }}>Add practice task</h3>
          <div className="small muted">Linked to {pieceName(entry)}</div>
        </div>
        <button type="button" className="quiet" onClick={onClose}>Close</button>
      </div>
      <label>
        <span className="small">What should they practise?</span>
        <input required maxLength={200} value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label>
        <span className="small">Suggested daily practice (optional)</span>
        <input
          type="number"
          min={1}
          max={480}
          value={suggestedMinutes}
          onChange={(event) => setSuggestedMinutes(event.target.value)}
          placeholder="e.g. 10"
        />
        <span className="small muted" style={{ display: "block", marginTop: 3 }}>
          A guide for each practice day, not a countdown or a limit.
        </span>
      </label>
      <label>
        <span className="small">Extra detail (optional)</span>
        <textarea rows={3} maxLength={2000} value={description} onChange={(event) => setDescription(event.target.value)} />
      </label>
      {problem && <div className="error small">{problem}</div>}
      {saved && <ActionConfirmation variant="success" title="Practice task added" />}
      <div className="row repertoire-form-row">
        <button type="submit" disabled={busy || title.trim() === ""}>{busy ? "Adding…" : "Add to practice"}</button>
      </div>
    </form>
  );
}
